import { InMemoryCache, TypePolicies } from "@apollo/client";
import { offsetLimitPagination } from "@apollo/client/utilities";

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      chatThreads: offsetLimitPagination(),
      eltSync: {
        // The `EltSyncs` list already holds every sync; without this each `eltSync(id)` is its own
        // cache key and so its own request, one per row of the sources list.
        read(_existing, { args, toReference }) {
          if (typeof args?.id !== "string") return undefined;
          return toReference({ __typename: "EltSync", id: args.id });
        },
      },
      modelsByIds: {
        /**
         * Resolve straight from the normalized `Model` entities instead of caching a list per exact
         * set of ids.
         *
         * Without this, asking for one more id is a different cache key, so the whole batch
         * refetches and every caller sees `data: undefined` until it lands. Models already in the
         * cache — most of them, since `PublishedModels` is prefetched with the editor route — are
         * now returned with no request at all.
         *
         * The `canRead` guard is what makes an unknown id still reach the network. A list returned
         * from `read` has its dangling references silently dropped and is then reported as
         * complete, so without the guard an id the cache has never seen would never be fetched, and
         * the caller would see it as deleted. Returning undefined instead makes the field a miss.
         * Entities that exist but lack a selected field need no guard — the normal read handles
         * those.
         */
        keyArgs: false,
        read(_existing, { args, toReference, canRead }) {
          const ids = args?.ids;
          if (!Array.isArray(ids)) return undefined;
          const refs = ids.map((id) =>
            toReference({ __typename: "Model", id }),
          );
          if (refs.some((ref) => !ref || !canRead(ref))) return undefined;
          return refs;
        },
      },
    },
  },
};

export const createApolloCache = () => new InMemoryCache({ typePolicies });
